import type { FC, MouseEvent } from "react";
import { cx } from "@stubhub/broadway-foundation/css";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import { SoccerIcon } from "@stubhub/broadway-web/icons/functional";
import {
  EventGridListItemTitleTeamVsTeamFlag,
  EventGridListItem,
  EventGridListItemBody,
  EventGridListItemCalendar,
  EventGridListItemContent,
  EventGridListItemContentFieldType,
  EventGridListItemTitle,
  EventGridListItemSubtitle,
} from "@stubhub/ensemble-discovery";
import { useContent } from "src/shared/components/content";
import {
  calenderStyles,
  contentStyles,
  eventEntryFallbackFlagRecipe,
  listItemContainerClass,
  titleStyles,
} from "./event-list-carousel.recipes";

export interface EventEntryProps {
  id: number;
  /** Cobrand-aware event URL returned by the API. Falls back to `/event/${id}` when absent. */
  url?: string;
  homeTeamCountryCode: string;
  homeTeamCountryAbr: string;
  awayTeamCountryCode: string;
  awayTeamCountryAbr: string;
  matchNumber: string;
  startTimeMillis: number;
  venueTimeZoneOffset: number;
  venueName: string;
  venueLocation: string;
  onClick: (e: MouseEvent<Element>) => void;
}

/**
 * EventEntry — example render-item component for EventListCarousel.
 * Renders a team row with image, team name, and next event date.
 */
export const EventEntry: FC<EventEntryProps> = ({
  homeTeamCountryCode,
  homeTeamCountryAbr,
  awayTeamCountryCode,
  awayTeamCountryAbr,
  matchNumber,
  startTimeMillis,
  venueTimeZoneOffset,
  venueName,
  venueLocation,
  onClick,
  id,
  url,
}) => {
  const { fallbackFlagContainer, fallbackFlag } = eventEntryFallbackFlagRecipe();
  const tbdText = useContent("cid$#3983126#");
  return (
    <EventGridListItem
      eventTitle={`Match ${matchNumber}`}
      href={url ?? `/event/${id}`}
      isDateConfirmed
      isTimeConfirmed
      onClick={onClick}
      startTimeMillis={startTimeMillis}
      venueCountryCode="US"
      venueLocation={venueLocation}
      venueName={venueName}
      venueTimeZoneOffsetMillis={venueTimeZoneOffset}
      classes={{ container: listItemContainerClass }}
    >
      <EventGridListItemCalendar classes={calenderStyles} />
      <EventGridListItemBody classes={contentStyles}>
        <EventGridListItemTitle>
          <EventGridListItemTitleTeamVsTeamFlag
            classes={titleStyles}
            homeTeamCountryCode={(homeTeamCountryCode || undefined) as FlagCountry | undefined}
            awayTeamCountryCode={(awayTeamCountryCode || undefined) as FlagCountry | undefined}
            homeTeamShortName={homeTeamCountryAbr || tbdText}
            awayTeamShortName={awayTeamCountryAbr || tbdText}
            fallbackFlag={
              <span className={fallbackFlagContainer}>
                <SoccerIcon classes={{ root: cx(fallbackFlag) }} size={12} />
              </span>
            }
          />
        </EventGridListItemTitle>
        <EventGridListItemSubtitle>
          <EventGridListItemContent
            fields={[
              { type: EventGridListItemContentFieldType.Time },
              { type: EventGridListItemContentFieldType.VenueLocation },
            ]}
          />
        </EventGridListItemSubtitle>
        <EventGridListItemSubtitle>
          <EventGridListItemContent fields={[{ type: EventGridListItemContentFieldType.VenueName }]} />
        </EventGridListItemSubtitle>
      </EventGridListItemBody>
    </EventGridListItem>
  );
};
